import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { fetchGetOrgUnitsByUserIdTree, fetchGetUserResponsibilities } from '@/service/api';
import { localStg } from '@/utils/storage';
import { SetupStoreId } from '@/enum';
import { useAuthStore } from '../auth';

export const useOrgStore = defineStore(SetupStoreId.Org, () => {
  const authStore = useAuthStore();

  // 当前选择的组织代码
  const currentOrgCode = ref<string>(localStg.get('selectedOrgCode') || '');
  // 当前选择的部门代码
  const currentDeptCode = ref<string>(localStg.get('selectedDeptCode') || '');

  // 默认组织代码（从登录接口返回）
  const defaultOrgCode = ref<string>(localStg.get('defaultOrgCode') || '');

  // 组织架构树
  const orgUnitsTree = ref<Api.SystemManage.OrgUnitsTree[]>([]);

  // 用户所属组织信息
  const userResponsibilities = ref<Api.SystemManage.UserResponsibilities | null>(null);

  // 当前组织信息
  const currentOrgInfo = ref<{ id?: string; name?: string; code?: string } | null>(null);

  // 当前部门信息
  const currentDeptInfo = ref<{ id?: string; name?: string; code?: string } | null>(null);

  // 加载状态
  const loading = ref(false);

  // 计算属性：当前组织显示名称
  const currentOrgDisplayName = computed(() => {
    // if (currentOrgInfo.value && currentDeptInfo.value) {
    //   // 如果有部门信息，显示为 组织+部门
    //   return `${currentOrgInfo.value.name} - ${currentDeptInfo.value.name}`;
    // } else if (currentOrgInfo.value) {
    //   // 只有组织信息，只显示组织
    //   return currentOrgInfo.value.name;
    // }
    return localStg.get('selectedOrgName') || '';
  });

  // 设置当前组织代码
  function setCurrentOrgCode(id: string, name: string, orgCode: string) {
    currentOrgCode.value = orgCode;
    localStg.set('selectedOrgCode', orgCode);
    localStg.set('selectedOrgId', id);
    localStg.set('selectedOrgName', name);
    currentOrgInfo.value = {
      id,
      name,
      code: orgCode
    };

    // updateCurrentOrgInfo();
  }

  // 设置当前部门代码
  function setCurrentDeptCode(deptId?: string, deptName?: string, deptOrgCode?: string) {
    currentDeptCode.value = deptOrgCode || '';
    localStg.set('selectedDeptCode', deptOrgCode || '');
    localStg.set('selectedDeptId', deptId || '');
    localStg.set('selectedDeptName', deptName || '');
    currentDeptInfo.value = {
      id: deptId,
      name: deptName,
      code: deptOrgCode
    };

    // updateCurrentDeptInfo();
  }

  // 设置默认组织代码
  function setDefaultOrgCode(orgCode: string) {
    defaultOrgCode.value = orgCode;
  }

  // 获取默认组织代码，根据字符长度判断是组织还是部门
  function getDefaultOrgCode(): { orgCode: string; deptCode: string; isOrg: boolean; isDept: boolean } {
    if (!defaultOrgCode.value) {
      return { orgCode: '', deptCode: '', isOrg: false, isDept: false };
    }

    // 根据字符长度判断：大于4个字符为部门，否则为组织
    const isDept = defaultOrgCode.value.length > 4;
    const isOrg = !isDept;

    return {
      orgCode: isOrg ? defaultOrgCode.value : '',
      deptCode: isDept ? defaultOrgCode.value : '',
      isOrg,
      isDept
    };
  }

  // 更新当前组织信息
  function updateCurrentOrgInfo() {
    if (!currentOrgCode.value || !orgUnitsTree.value.length) {
      currentOrgInfo.value = null;
      return;
    }

    const findOrgByCode = (orgs: Api.SystemManage.OrgUnitsTree[], code: string): Api.SystemManage.OrgUnitsTree | null => {
      for (const org of orgs) {
        if (org.code === code) {
          return org;
        }
        if (org.children) {
          const found = findOrgByCode(org.children, code);
          if (found) return found;
        }
      }
      return null;
    };

    const org = findOrgByCode(orgUnitsTree.value, currentOrgCode.value);
    if (org) {
      currentOrgInfo.value = {
        id: org.id,
        name: org.name,
        code: org.code
      };
    } else {
      currentOrgInfo.value = null;
    }
  }

  // 更新当前部门信息
  function updateCurrentDeptInfo() {
    if (!currentDeptCode.value || !orgUnitsTree.value.length) {
      currentDeptInfo.value = null;
      return;
    }

    const findDeptByCode = (orgs: Api.SystemManage.OrgUnitsTree[], code: string): Api.SystemManage.OrgUnitsTree | null => {
      for (const org of orgs) {
        if (org.children) {
          for (const dept of org.children) {
            if (dept.code === code) {
              return dept;
            }
          }
        }
      }
      return null;
    };

    const dept = findDeptByCode(orgUnitsTree.value, currentDeptCode.value);
    if (dept) {
      currentDeptInfo.value = {
        id: dept.id,
        name: dept.name,
        code: dept.code
      };
    } else {
      currentDeptInfo.value = null;
    }
  }

  // 加载组织架构树
  async function loadOrgUnitsTree() {
    try {
      loading.value = true;
      const { data } = await fetchGetOrgUnitsByUserIdTree();
      orgUnitsTree.value = data || [];
      updateCurrentOrgInfo();
      updateCurrentDeptInfo();
    } catch {
      window.$message?.error('加载组织架构树失败');
    } finally {
      loading.value = false;
    }
  }

  // 加载用户组织信息
  async function loadUserResponsibilities() {
    if (!authStore.userInfo.id) {
      window.$message?.warning('用户ID不存在，无法获取用户所属组织信息');
      return;
    }

    try {
      const { data } = await fetchGetUserResponsibilities(authStore.userInfo.id);
      userResponsibilities.value = data;
    } catch {
      window.$message?.error('获取用户所属组织信息失败');
    }
  }

  // 初始化组织信息
  async function initOrgInfo() {
    // await loadOrgUnitsTree();
    // await loadUserResponsibilities();
    currentOrgCode.value = localStg.get('selectedOrgCode') || '';
    currentOrgInfo.value = {
      id: localStg.get('selectedOrgId') || '',
      name: localStg.get('selectedOrgName') || '',
      code: localStg.get('selectedOrgCode') || ''
    };
    currentDeptInfo.value = {
      id: localStg.get('selectedDeptId') || '',
      name: localStg.get('selectedDeptName') || '',
      code: localStg.get('selectedDeptCode') || ''
    };
  }

  // 重置store
  function resetStore() {
    const orgStore = useOrgStore();
    localStg.remove('selectedOrgCode');
    localStg.remove('selectedDeptCode');
    localStg.remove('selectedDeptName');
    localStg.remove('selectedDeptId');
    localStg.remove('selectedOrgId');
    localStg.remove('selectedOrgName');
    localStg.remove('orgbestSignAccount');
    localStg.remove('orgUnifiedCode');
    defaultOrgCode.value = ''; // 清除默认组织代码
    orgStore.$reset();
  }

  return {
    currentOrgCode,
    currentDeptCode,
    defaultOrgCode,
    orgUnitsTree,
    userResponsibilities,
    currentOrgInfo,
    currentDeptInfo,
    loading,
    currentOrgDisplayName,
    setCurrentOrgCode,
    setCurrentDeptCode,
    setDefaultOrgCode,
    getDefaultOrgCode,
    updateCurrentOrgInfo,
    updateCurrentDeptInfo,
    loadOrgUnitsTree,
    loadUserResponsibilities,
    initOrgInfo,
    resetStore
  };
});
